import { Phone, Mail, Clock, MapPin } from "lucide-react";
import SocialLinks from "@/components/Socials";
import PageHero from "@/components/PageHero";
import EstimateForm from "@/components/EstimateForm";
import { Reveal } from "@/components/Reveal";
import { BUSINESS, SERVICE_AREAS } from "@/data/site";

export default function Contact() {
  return (
    <>
      <PageHero
        eyebrow="Contact Us"
        title="Let us know how our welding services can help"
        lead="Tell us about your project and we'll follow up with a free estimate. Prefer to talk? We're only a phone call away."
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <div className="bg-coal border border-line p-6 sm:p-10">
                <h2 className="font-display text-2xl font-bold uppercase text-slate-100">
                  Request a Free Estimate
                </h2>
                <p className="text-sm text-slate-400 mt-2 mb-8">
                  Free estimates within 100 miles of Hardin, MT.
                </p>
                <EstimateForm />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <Reveal delay={0.08}>
              <div className="bg-coal border border-line p-6 sm:p-8 space-y-5">
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="contact-phone-link"
                  className="flex items-start gap-3.5 text-slate-100 hover:text-gold transition-colors"
                >
                  <Phone size={18} className="text-gold mt-1 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                      Phone
                    </span>
                    <span className="block font-display text-2xl font-bold mt-1">{BUSINESS.phone}</span>
                  </span>
                </a>
                <a
                  href={BUSINESS.emailHref}
                  data-testid="contact-email-link"
                  className="flex items-start gap-3.5 text-slate-100 hover:text-gold transition-colors break-all"
                >
                  <Mail size={18} className="text-gold mt-1 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                      Email
                    </span>
                    <span className="block text-sm mt-1">{BUSINESS.email}</span>
                  </span>
                </a>
                <div className="flex items-start gap-3.5 text-slate-100">
                  <Clock size={18} className="text-gold mt-1 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                      Business Hours
                    </span>
                    {BUSINESS.hours.map((h) => (
                      <span key={h.days} className="block text-sm mt-1">
                        {h.days}: {h.time}
                      </span>
                    ))}
                  </span>
                </div>
                <div className="pt-2">
                  <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">
                    Follow Us
                  </span>
                  <div className="flex items-center gap-3">
                    <SocialLinks testIdPrefix="contact-social" boxClass="w-9 h-9" iconSize={15} />
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="bg-coal border border-line p-6 sm:p-8 bg-grid">
                <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold flex items-center gap-2">
                  <MapPin size={14} aria-hidden="true" />
                  Service Area
                </h3>
                <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
                  {SERVICE_AREAS.map((area) => (
                    <li key={area} className="text-sm text-slate-300">
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
